import React, { useState } from 'react';
import { Alert, Button, Empty, List, Spin, Tag, Typography } from 'antd';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  IntegrationMessage,
  useOrganizationIntegrationMessages
} from '../../../../components/Integrations/integrationMessages';
import styles from './IntegrationMessagesPanel.module.css';

type Props = {
  organizationId: number;
  integrationId: string | number;
  integrationName: string;
};

type Filter = 'all' | 'errors';

const levelColor = {
  error: 'error',
  warning: 'warning',
  info: 'processing'
};

const IntegrationMessagesPanel: React.FC<Props> = ({
  organizationId,
  integrationId,
  integrationName
}) => {
  const [filter, setFilter] = useState<Filter>('all');
  const { messages, loading, error } = useOrganizationIntegrationMessages(
    '',
    organizationId,
    integrationId
  );
  const visibleMessages =
    filter === 'errors'
      ? messages.filter(message => message.level === 'error')
      : messages;

  const projectContext = (message: IntegrationMessage) => {
    const label = message.project_name || `Project ${message.project_id}`;

    if (!message.project_id || !message.task_local_id) return label;

    return (
      <a
        href={`/projects/${message.project_id}/tasks/${message.task_local_id}`}
      >
        {label} · BH-{message.task_local_id}
      </a>
    );
  };

  return (
    <div className={styles.page}>
      <Link
        to={`/organizations/${organizationId}/integrations/${integrationId}/edit`}
      >
        <Button type="link" className={styles.navigateBack}>
          {`< Back to ${integrationName}`}
        </Button>
      </Link>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{integrationName} activity log</h2>
          <p className={styles.description}>
            Messages from this integration across your projects.
          </p>
        </div>
        <div className={styles.filters}>
          <Button
            shape="round"
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            shape="round"
            type={filter === 'errors' ? 'primary' : 'default'}
            onClick={() => setFilter('errors')}
          >
            Errors
          </Button>
        </div>
      </div>

      {error && (
        <Alert
          showIcon
          type="error"
          message="Integration activity could not be loaded."
        />
      )}

      <Spin spinning={loading}>
        <List
          className={styles.list}
          dataSource={visibleMessages}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  filter === 'errors'
                    ? 'No recent integration errors'
                    : 'No recent integration activity'
                }
              />
            )
          }}
          renderItem={message => (
            <List.Item key={message.id}>
              <div className={styles.message}>
                <div className={styles.messageHeader}>
                  <Tag color={levelColor[message.level]}>
                    {message.level.toUpperCase()}
                  </Tag>
                  <span className={styles.subject}>{message.subject}</span>
                </div>
                {message.message && (
                  <Typography.Paragraph
                    className={styles.details}
                    ellipsis={{ rows: 2, expandable: true, symbol: 'More' }}
                  >
                    {message.message}
                  </Typography.Paragraph>
                )}
                <div className={styles.context}>
                  <span className={styles.contextItem}>
                    Project: {projectContext(message)}
                  </span>
                  {message.user_name && (
                    <span className={styles.contextItem}>
                      {message.user_name}
                    </span>
                  )}
                  <span className={styles.contextItem}>
                    {formatDistanceToNow(parseISO(message.created_at), {
                      addSuffix: true
                    })}
                  </span>
                </div>
              </div>
            </List.Item>
          )}
        />
      </Spin>
    </div>
  );
};

export default IntegrationMessagesPanel;
